import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useEffect, useMemo, useState } from 'react';
import { DataSeries, DemeterEmailSubscriptionType, ListLivestockInsurancePricesResponse } from '../../../../../Generated/Raven-Demeter';
import PageAccordion from '../../../../Components/Form/Buttons/Accordions/PageAccordion';
import Table from '../../../../Components/Tables/Table/Table';
import TableWrapper from '../../../../Components/Tables/TableWrapper/TableWrapper';
import tableCsvDownloadService from '../../../../Services/Download/DownloadService';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { LiquidityRiskCalculationInputs } from '../../RiskDefinitions';
import styles from '../InsuranceQuoteEstimator.module.scss';
import { coverageTableHeaders, CoverageTableRow } from '../InsuranceQuoteEstimatorDefinitions';
import CoverageLevelsActions from './CoverageLevelsActions';

interface ICoverageLevelsProps {
    calculationInputs: LiquidityRiskCalculationInputs;
    listLivestockInsurancePricesResponse: ListLivestockInsurancePricesResponse | undefined;
    accordionOpen: boolean;
}

const defaultCurrencySignificantDigits = 2;
const largeValueSignificantDigits = 0;
const defaultPercentSignificantDigits = 0;
const percentMultiplier = 0.01;
const defaultMinimumTableWidth = 900;
const weekInMilliseconds = 7 * 24 * 3600 * 1000;

const CoverageLevels: React.FC<ICoverageLevelsProps> = (props: ICoverageLevelsProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Display hooks.
    const [openEndorsements, setOpenEndorsements] = useState<number[]>([]);

    useEffect(() => {
        setOpenEndorsements([]);
    }, [props.listLivestockInsurancePricesResponse]);

    const coverageTableRows: CoverageTableRow[] | undefined = useMemo(() => {
        if (!props.listLivestockInsurancePricesResponse) {
            return [];
        }

        return props.listLivestockInsurancePricesResponse.rows
            ?.sort((a, b) => a.endorsementLength - b.endorsementLength)
            .map((x, index, array) => {
                const insuredValue =
                    props.calculationInputs.animalHeadCount *
                    props.calculationInputs.endWeight *
                    x.coveragePrice *
                    props.calculationInputs.insuredSharedPercentage *
                    percentMultiplier;
                const totalPremium = insuredValue * x.rate * x.coverageLevelPercent * percentMultiplier;
                const subsidy = x.subsidyPercent * percentMultiplier * totalPremium;
                const producerPremium = x.rate * insuredValue - subsidy;
                const producerCostPerHundredWeight = producerPremium / props.calculationInputs.endWeight;
                const producerCostPerHead = producerPremium / props.calculationInputs.animalHeadCount;
                const isKey = index === 0 || x.endorsementLength !== array[index - 1].endorsementLength;
                const isOpen = openEndorsements.some((y) => y === x.endorsementLength);
                const isVisible = isKey || isOpen;
                const endCoverageDate = new Date(new Date(x.asOfDate).getTime() + weekInMilliseconds * x.endorsementLength);

                return {
                    isKey,
                    isVisible,
                    endorsementLength: isKey ? (
                        <button
                            className={styles.coverage_levels_table_endorsement_length_button}
                            type="button"
                            onClick={() => {
                                if (openEndorsements.includes(x.endorsementLength)) {
                                    setOpenEndorsements(openEndorsements.filter((y) => y !== x.endorsementLength));
                                    return;
                                }

                                setOpenEndorsements([...openEndorsements, x.endorsementLength]);
                            }}
                        >
                            {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            <p>{x.endorsementLength}</p>
                        </button>
                    ) : null,
                    endorsementLengthRawValue: x.endorsementLength,
                    commodityType: translations.commodity[x.subCommodity],
                    asOfDate: formattingService.toShortDayMonthYear(x.asOfDate),
                    endCoverageDate: formattingService.toShortDayMonthYear(endCoverageDate),
                    expectedValue: x.expectedValue,
                    coverageLevelPercent: x.coverageLevelPercent,
                    coveragePrice: x.coveragePrice,
                    rate: x.rate,
                    insuredValue,
                    totalPremium,
                    subsidy,
                    producerPremium,
                    producerCostPerHundredWeight,
                    producerCostPerHead,
                };
            });
    }, [openEndorsements]);

    const buildCsv = (): DataSeries[] | undefined => {
        if (!coverageTableRows || coverageTableRows?.length === 0) {
            return [];
        }

        const newData = coverageTableHeaders.map((row) => ({
            label: `${row.headerValueGetter!()}`,
            data: [
                ...coverageTableRows!.map((x, index) => ({
                    value: `${x[row.field as keyof CoverageTableRow]}`,
                    date: `${x.asOfDate}(${index + 1})`,
                    rawDate: new Date(x.asOfDate)!,
                    isActualValue: true,
                })),
            ],
        }));

        return newData;
    };

    const handleExportButtonClick = () => {
        const downloadData = buildCsv();
        tableCsvDownloadService.downloadCsv(downloadData!, translations.risk.insuranceQuoteEstimator.text.coverageLevels);
    };

    return (
        <div className={styles.liquidity_risk_section}>
            <PageAccordion
                title={translations.risk.insuranceQuoteEstimator.headers.coverageLevels}
                disabled={!props.listLivestockInsurancePricesResponse && !props.accordionOpen}
                open={props.accordionOpen}
                loading={!props.listLivestockInsurancePricesResponse && props.accordionOpen}
                component={
                    <div className={styles.coverage_levels_table}>
                        <TableWrapper
                            isLoading={!props.listLivestockInsurancePricesResponse}
                            headerOptions={{
                                isCsvDownloadAvailable: true,
                                isXlsxDownloadAvailable: true,
                                handleExportButtonClick,
                            }}
                            rightSide={
                                <CoverageLevelsActions
                                    calculationInputs={props.calculationInputs}
                                    subscriptionType={DemeterEmailSubscriptionType.LivestockInsurancePriceSubscription}
                                />
                            }
                            track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'ListCoverageLevels' }}
                        >
                            <Table
                                tableHeaders={coverageTableHeaders}
                                tableData={coverageTableRows!}
                                minimumTableWidth={defaultMinimumTableWidth}
                                displayDecimalPlaces={defaultCurrencySignificantDigits}
                            />
                        </TableWrapper>
                    </div>
                }
            />
        </div>
    );
};

export default CoverageLevels;
